import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Box } from '@mui/joy';

/**
 * Fit mode for video scaling
 * - 'fitWidth': Scale to container width, maintain aspect ratio
 * - 'fitHeight': Scale to container height, maintain aspect ratio
 * - 'stretch': If landscape (width > height), use fitHeight, otherwise use fitWidth
 */
export type FitMode = 'fitWidth' | 'fitHeight' | 'stretch';

export interface VideoScalerProps {
  fitMode: FitMode;
  /**
   * Aspect ratio (width / height) for the display area
   * Default: 1.777777 (16:9)
   * Set to 0 or null to ignore aspect ratio
   *
   * NOTE: This is the CONFIGURED aspect ratio for the wrapper.
   * The video element will maintain its NATIVE aspect ratio within this wrapper.
   */
  aspectRatio?: number | null;
  backgroundColor?: string;
  className?: string;
  videoClassName?: string;
  children?: React.ReactNode;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  videoProps?: React.VideoHTMLAttributes<HTMLVideoElement>;
  onDimensionsCalculated?: (dimensions: { width: number; height: number }) => void;
  isInPip?: boolean;
}

const calculateDimensions = (
  containerWidth: number,
  containerHeight: number,
  fitMode: FitMode,
  aspectRatio: number | null
): { width: number; height: number } => {
  // Handle edge cases
  if (containerWidth <= 0 || containerHeight <= 0) {
    return { width: 0, height: 0 };
  }

  if (!aspectRatio || aspectRatio === 0) {
    return { width: containerWidth, height: containerHeight };
  }

  if (fitMode === 'fitWidth') {
    // Fit Width: Width = container width, Height = container width * (1 / aspectRatio)
    // For 16:9 (1.777777): Height = container width * 0.5625
    const width = containerWidth;
    const height = containerWidth * (1 / aspectRatio);
    return { width, height };
  }

  if (fitMode === 'fitHeight') {
    // Fit Height: Height = container height, Width = container height * aspectRatio
    // For 16:9 (1.777777): Width = container height * 1.777777
    const height = containerHeight;
    const width = containerHeight * aspectRatio;
    return { width, height };
  }

  if (fitMode === 'stretch') {
    // Stretch: Fill as much as possible while maintaining aspect ratio
    const containerRatio = containerWidth / containerHeight;

    if (containerRatio > aspectRatio) {
      // Container is wider than video -> Height is the constraint
      const height = containerHeight;
      const width = containerHeight * aspectRatio;
      return { width, height };
    }

    // Container is narrower/taller than video -> Width is the constraint
    const width = containerWidth;
    const height = containerWidth * (1 / aspectRatio);
    return { width, height };
  }

  return { width: containerWidth, height: containerHeight };
};

const VideoScaler: React.FC<VideoScalerProps> = ({
  isInPip = false,
  fitMode,
  aspectRatio = 1.777777,
  backgroundColor = '#515358',
  className,
  videoClassName,
  children,
  videoRef,
  videoProps,
  onDimensionsCalculated
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  // Update container dimensions from ResizeObserver or explicit props
  const updateContainerDimensions = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const { width, height } = rect;

      setContainerDimensions((prev) => {
        if (width !== prev.width || height !== prev.height) {
          return { width, height };
        }
        return prev;
      });
    }
  }, []);

  const calculatedDimensions = calculateDimensions(
    containerDimensions.width,
    containerDimensions.height,
    fitMode,
    aspectRatio
  );

  // Notify parent of calculated dimensions
  useEffect(() => {
    if (calculatedDimensions.width > 0 && calculatedDimensions.height > 0) {
      onDimensionsCalculated?.(calculatedDimensions);
    }
  }, [calculatedDimensions, onDimensionsCalculated]);

  // Set up ResizeObserver for container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measureContainer = () => {
      updateContainerDimensions();
    };

    // Initial measurement
    measureContainer();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        updateContainerDimensions();
      });

      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
      };
    }

    // Fallback for browsers without ResizeObserver
    const handleResize = () => {
      updateContainerDimensions();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateContainerDimensions]);

  /**
   * objectFit strategy:
   * - 'contain': Video maintains its NATIVE aspect ratio within the wrapper
   *   This means if video is 4:3 but wrapper is 16:9, video will be letterboxed
   *   Background color shows around video
   * - 'fill': Video fills wrapper exactly, may distort if native AR ≠ wrapper AR
   *   Only used when stretch mode actually stretches (not used anymore since stretch now uses fitWidth/fitHeight)
   */
  const objectFit = 'contain';

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Video wrapper: Sized to configured aspect ratio */}
      <Box
        ref={videoWrapperRef}
        className={videoClassName}
        sx={{
          position: 'absolute',
          ...(calculatedDimensions.width > 0 && calculatedDimensions.height > 0
            ? {
                width: `${calculatedDimensions.width}px`,
                height: `${calculatedDimensions.height}px`,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)'
              }
            : {
                width: 0,
                height: 0,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)'
              }),
          zIndex: 1
        }}
      >
        {videoRef && videoProps ? (
          <video
            ref={videoRef}
            {...videoProps}
            style={{
              width: '100%',
              height: '100%',
              objectFit,
              display: 'block',
              ...videoProps.style
            }}
          />
        ) : (
          children
        )}
      </Box>
    </Box>
  );
};

export default VideoScaler;
